import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import classes from "./UsersManagement.module.css";
import { useNavigate, useParams } from "react-router-dom";
import CommonCard from "../../components/ui/CommonCard/CommonCard";
import { MdOutlineEmail, MdOutlineLocalPhone } from "react-icons/md";
import Table from "../../components/Table/Table";
import axios from "axios";
import { env } from "../../constants/env";

const statusOptions = [
  { value: "Inactive", label: "Mark as Inactive" },
  { value: "Active", label: "Mark as Active" },
];

const statusStyles = {
  Active: {
    color: "#12AD01",
    backgroundColor: "#D8F7D4",
  },
  Inactive: {
    color: "#545454",
    backgroundColor: "#EAEAEA",
  },
};

const UsersManagement = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("Active");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const columns = [
    // { key: "id", label: "ID" },
    { key: "car", label: "CAR" },
    { key: "date", label: "DATE" },
    { key: "status", label: "STATUS" },
    { key: "amount", label: "AMOUNT" },
  ];

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${env.apiUrl}/api/users/${id}`);
        setUser(res.data);
        setSelectedStatus(res.data.status);
      } catch (err) {
        console.error("Error fetching user:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    const prevStatus = selectedStatus;
    setSelectedStatus(newStatus);

    try {
      await axios.patch(`${env.apiUrl}/api/users/${id}/status`, {
        status: newStatus,
      });
    } catch (err) {
      console.error("Failed to update user status:", err);
      setSelectedStatus(prevStatus); // revert back on error
      toast.error("Failed to update status. Please try again.");
    }
  };

  if (loading || !user) return <div>Loading user details...</div>;

  return (
    <div className={classes.bookingsManagement}>
      <div
        className={`${classes.headCard} d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3`}
      >
        <div className={classes.cardRevenueHeader}>
          <h5>User: {user.id.toString().slice(-5).toUpperCase()}</h5>
        </div>
        <div className={classes.statusSelector}>
          <div className="position-relative">
            <div
              className={`form-select ${classes.customSelect}`}
              style={statusStyles[selectedStatus]}
            >
              {selectedStatus}
            </div>
            <select
              className="form-select position-absolute top-0 start-0 opacity-0"
              value={selectedStatus}
              onChange={handleStatusChange}
            >
              {statusOptions.map((status, i) => (
                <option key={i} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Profile Info */}
        <div className="col-md-6">
          <CommonCard className={classes.bookingDetails}>
            <div className={classes.userDetails}>
              <h3>Profile information</h3>
              <div
                className={`${classes.userInfo} d-flex align-items-center gap-2 my-3`}
              >
                <div
                  className={classes.avatarContainer}
                  style={{
                    backgroundImage: `url(${user.avatar})`,
                  }}
                />
                <div>
                  <p className={classes.userName}>{user.name}</p>
                  <p className={classes.userId}>
                    #{user.id.toString().slice(-5).toUpperCase()}
                  </p>
                </div>
              </div>
              <div
                className={`${classes.userInfo} d-flex align-items-center gap-2 mb-3`}
              >
                <MdOutlineEmail size={17} /> <p>{user.email}</p>
              </div>
              <div
                className={`${classes.userInfo} d-flex align-items-center gap-2`}
              >
                <MdOutlineLocalPhone size={17} /> <p>{user.phone}</p>
              </div>
            </div>
          </CommonCard>
        </div>

        {/* Address & License Info */}
        <div className="col-md-6">
          <CommonCard className={classes.customerCard}>
            <div className={`d-flex flex-column ${classes.carInfo}`}>
              <h3>Address</h3>
              <p className={classes.carName}>{user.address.line1}</p>
              <p className={classes.carName}>
                {user.address.city}, {user.address.zip}
              </p>
              <p className={classes.carName}>{user.address.country}</p>
            </div>
          </CommonCard>

          <CommonCard className={`mt-3 ${classes.customerCard}`}>
            <div className={`d-flex flex-column ${classes.carInfo}`}>
              <h3>Driver’s license</h3>
            </div>
            <div className={`d-flex flex-column ${classes.carDetails}`}>
              <div className="d-flex align-items-center justify-content-between">
                <h4>License #</h4>
                <p>{user.license.number}</p>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <h4>End date</h4>
                <p>{user.license.expiry}</p>
              </div>
            </div>
          </CommonCard>
        </div>
      </div>

      {/* Bookings Table */}
      <div className={classes.bookingsContainer}>
        <h4 className="mb-3">Bookings</h4>
        <Table
          dataType={"Bookings"}
          data={user.bookings}
          columns={columns}
          showDialog={showTableDialog}
          setShowDialog={setShowTableDialog}
          bookingInfo={() =>
            navigate(
              `/bookings/bookings-management/${user.bookings[selectedBooking]?.id}`
            )
          }
          clickedIndex={(value) => setSelectedBooking(value)}
        />
      </div>
    </div>
  );
};

export default UsersManagement;
